import { ref, getCurrentInstance } from 'vue';
import store from '@/store';

const DEFAULT_PROVINCIA_ID = 30;

/**
 * Owns the "localidades by provincia" lookup used by the cascading
 * Provincia → Localidad control.
 *
 * Why it exists: `Locations.vue` used to dispatch straight from the component,
 * so presentation and store access were mixed AND two fast provincia changes
 * could race — the older response could land last and overwrite `locations`
 * with data for the previous provincia. This composable keeps a request token
 * so only the response that still corresponds to the current provincia is
 * applied.
 *
 * `invalidate()` / `applyLocations()` exist so the component can move to `null`
 * or to a cached list through the composable instead of assigning the ref
 * directly: assigning directly would leave an in-flight request alive, and its
 * late response would repaint the previous provincia's data.
 *
 * @returns {Object} Reactive state plus the loader and the invalidation hooks.
 */
export function useLocations() {
    const instance = getCurrentInstance();
    const toast = instance?.proxy?.$toast;

    const locations = ref([]);
    const loading = ref(false);
    const error = ref(null);

    // Monotonic token. Only the response whose token still equals the latest
    // one is applied; anything older is a stale race and is discarded.
    let requestId = 0;

    function resolveProvinciaId(provincia) {
        if (provincia == null) {
            return null;
        }
        if (typeof provincia === 'number') {
            return provincia;
        }
        return provincia.id ?? DEFAULT_PROVINCIA_ID;
    }

    /**
     * Drops any in-flight request and resets the lifecycle to a clean, settled
     * state. Bumping the token makes the pending request obsolete, so it can no
     * longer apply its response — and because it also releases `loading`, the
     * control can never get stuck in a permanent spinner (the pending request's
     * own `finally` sees a stale token and deliberately does not touch it).
     */
    function invalidate() {
        requestId += 1;
        locations.value = [];
        loading.value = false;
        error.value = null;
    }

    /**
     * Applies a list that did NOT come from a request (a provincia's cached
     * `localidades`). Goes through `invalidate()` first so a pending request for
     * a previous provincia cannot land afterwards and overwrite the cache.
     *
     * @param {Array} list locations to apply.
     * @returns {Array} The applied list.
     */
    function applyLocations(list) {
        invalidate();
        locations.value = Array.isArray(list) ? list : [];

        return locations.value;
    }

    /**
     * Loads locations for a provincia.
     *
     * @param {Object|number|null} provincia provincia whose locations to load.
     * @returns {Promise<Array>} The applied locations, or [] if stale/failed.
     */
    const cargarLocations = async provincia => {
        const provinciaId = resolveProvinciaId(provincia);

        if (provinciaId == null) {
            // Sin provincia no hay consulta: se invalida lo que estuviera en
            // vuelo y se libera el lifecycle.
            invalidate();
            return [];
        }

        requestId += 1;
        const token = requestId;

        loading.value = true;
        error.value = null;

        try {
            const data = {
                filters: { provincia_id: provinciaId },
            };
            const response = await store.dispatch(
                store.getters.getConfig?.getVuexAlias?.('select.locations') || 'SELECT_LOCATIONS',
                data
            );

            // Stale response: a newer provincia was selected while this was in
            // flight. Applying it would show the previous provincia's data.
            if (token !== requestId) {
                return [];
            }

            locations.value = response.data.data;
            return locations.value;
        } catch (err) {
            if (token !== requestId) {
                return [];
            }

            error.value = err;
            locations.value = [];
            toast?.error(err?.message);
            return [];
        } finally {
            if (token === requestId) {
                loading.value = false;
            }
        }
    };

    return {
        locations,
        loading,
        error,
        cargarLocations,
        invalidate,
        applyLocations,
    };
}
